import React, { useEffect } from 'react'
import cls from 'classnames'
import styled from 'styled-components'

import { formatDate } from 'utils/dateUtils'
import ls from 'utils/LocalStorage'
import { atLeastStarterPlan } from 'utils/plans'
import { URL_CONST } from 'utils/urlConsts'

import { PLAN_TYPES } from 'containers/App/constants'
import {
  ALL_TIMES,
  INCIDENT_DATE_FILTERS_OPTIONS,
  LAST_24_HOURS,
} from 'containers/ResolutionCenter/IncidentFilters/constants'
import {
  attackThresholdOptions,
  markAsSpamActionOptions,
} from 'containers/Settings/Incidence/constants'

import GroupSettingsBanner from 'components/elements/GroupSettingsBanner'
import LargeBanner from 'components/elements/LargeBanner'
import Button from 'components/lib/DesignSystem/Button'
import Checkbox from 'components/lib/DesignSystem/Checkbox'
import { ArrowNext, FillWarning } from 'components/lib/DesignSystem/Icons'
import Select from 'components/lib/DesignSystem/Select'
import LockSign from 'components/lib/LockSign'
import Message from 'components/lib/Message'

import AttackActions from './AttackActions'
import ControllerRange from './ControllerRange'

function IncidentResponseSettings({
  className,
  data,
  history,
  licenses,
  ff,
  integrations,
  permissions,
  isMasterUser,

  updateSettingsField,
  updateDeepSettingsField,
  submitIncidentResponse,

  isGroupSettings,
  groupSettingsNameByPartner,

  isStaticSalesDemo,
}) {
  const handleScroll = (event) => {
    if (event.data.type === 'scroll' && history.action === 'POP') {
      document.getElementById(event.data.scrollToId).scrollIntoView(true)
      window.removeEventListener('message', handleScroll)
    }
  }

  useEffect(() => {
    window.addEventListener('message', handleScroll)
  }, [])

  useEffect(() => {
    const stateLocation = history.location.state
    if (stateLocation?.scrollToId && document.getElementById(stateLocation.scrollToId)) {
      document.getElementById(stateLocation.scrollToId).scrollIntoView(true)
      setTimeout(() => {
        const state = { ...stateLocation }
        delete state.scrollToId
        history.replace({ ...history.location, state })
      }, 2000)
    }
  }, [history])

  const setUnverifiedAction = (action) => updateSettingsField('attackAction', action)
  const setVerifiedAction = (action) => updateSettingsField('verifiedAttackAction', action)
  const setPhishingThreshold = (val) =>
    updateSettingsField('autoClassificationPhishingThreshold', val)
  const setSpamThreshold = (val) => updateSettingsField('autoClassificationSpamThreshold', val)

  const switchVipPhishingEnabled = () => {
    updateSettingsField(
      'autoClassificationVipPhishingEnabled',
      !data.autoClassificationVipPhishingEnabled
    )
  }

  const setVipPhishingThreshold = (val) =>
    updateSettingsField('autoClassificationVipPhishingThreshold', val)

  const setUnverifiedMessages = (id, message) =>
    updateDeepSettingsField('unverifiedAttackActionMsgs', id, message)
  const setVerifiedMessages = (id, message) =>
    updateDeepSettingsField('verifiedAttackActionMsgs', id, message)
  const setDefaultIncidentDateFilter = (val) =>
    updateSettingsField('defaultIncidentDateFilter', val)

  const handleSubmit = () => {
    const options = ls.getResolutionCenterOptions()
    ls.setResolutionCenterOptions({ ...options, toRefetchReport: true })
    submitIncidentResponse()
  }

  const hasOWA = integrations.o365

  const planIsStarter = licenses.plan === PLAN_TYPES.STARTER
  return (
    <div className={className}>
      {planIsStarter && (
        <LargeBanner
          text="Improve response times with our AI-powered incident response"
          subText="Enable automated incident detection and remediation"
          buttonText="Learn more"
          type="upgrade"
          id="settings-incidentResponse-upgrade"
        />
      )}

      <GroupSettingsBanner isGroupSettingsFlow={isGroupSettings} />

      <div
        className={cls({
          disabled_for_edit: !isGroupSettings && groupSettingsNameByPartner,
        })}
      >
        <div className="incident-section ptz">
          <span className="label-name">Scanning Options</span>
          {data.linkScanningLicense ? (
            <Checkbox
              id="Scan_URLs"
              checked={data.linkScanning}
              className="mt"
              onChange={() => updateSettingsField('linkScanning', !data.linkScanning)}
              labelText="Scan URLs"
            />
          ) : (
            <LockSign text="Scan URLs" style={{ marginBottom: '10px' }} />
          )}
          {data.fileScanningLicense ? (
            <Checkbox
              id="Scan_Attachments"
              checked={data.fileScanning}
              className="mt"
              onChange={() => updateSettingsField('fileScanning', !data.fileScanning)}
              labelText="Scan Attachments"
            />
          ) : (
            <LockSign text="Scan Attachments" />
          )}
          <div id="incident-section-scroll" />
        </div>

        <div className="incident-section">
          <span className="label-name">Reporting Options</span>
          {data.showInternalMessage && (
            <div>
              <Message>
                Note that there are cases where IS can&apos;t identify the Sender as
                &quot;internal&quot;, allowing the end-user to report those emails
              </Message>
            </div>
          )}
          <Checkbox
            checked={data.allowReportingInternal}
            className="mt"
            onChange={() =>
              updateSettingsField('allowReportingInternal', !data.allowReportingInternal)
            }
            labelText="Allow users to report internal emails"
            id="Allow_Reporting_on_internal_emails"
          />
          {hasOWA && (
            <Checkbox
              checked={data.confirmReportPopup}
              name="confirmReportPopup"
              className="mt"
              onChange={() => updateSettingsField('confirmReportPopup', !data.confirmReportPopup)}
              labelText="Display a confirmation pop-up when an email is being reported + Allow end-users to choose their reporting reason (OWA Addon only)"
              id="OWA_Confirmation_Pop-up"
            />
          )}
        </div>

        <div className="incident-section">
          <div
            className={cls('threshold-field', {
              'disabled-section': planIsStarter,
            })}
          >
            <span className="label-name">Incident Threshold</span>
            <span className="email-text">
              Each user report contributes to the score of the email threat level, which is based on
              the reporter’s awareness level. Scores are set using the index as following:
              <br />
              <strong className="blue">Beginner - 1, Mid - 5, Expert - 10</strong>
              <br />
              <br />
              Please select the threshold which determines when a user report will trigger the
              incident response
            </span>
            <div className="select">
              <Select
                value={!planIsStarter && data.attackThreshold}
                onSelect={(value) => updateSettingsField('attackThreshold', value)}
                options={attackThresholdOptions}
                id="attack_threshold"
                optionFilterProp="label"
              />
            </div>
          </div>
        </div>

        <div className="incident-section" id="automated-incident-section">
          <div
            className={cls('response-field', {
              'disabled-section': planIsStarter,
            })}
          >
            <span className="label-name">Automated Incident Response</span>

            {data.incidentResponseLastChangeDate && (
              <LargeBanner
                id="settings_GroupSettings_banner"
                type="warning"
                Icon={FillWarning}
                className="mt"
                text="It is important to note that these settings have recently been modified and should not be altered frequently as it can create instability."
                subText={`Modification details: User ${
                  data.incidentResponseLastChangeUser
                } Date: ${formatDate(data.incidentResponseLastChangeDate)}`}
              />
            )}

            <AttackActions
              action={data.attackAction}
              messages={data.unverifiedAttackActionMsgs || []}
              setAction={setUnverifiedAction}
              setMessages={setUnverifiedMessages}
              value="unverified"
              languages={data.languages || []}
              editorDisabled={planIsStarter || isStaticSalesDemo}
              integrations={integrations}
            />

            <AttackActions
              action={data.verifiedAttackAction}
              messages={data.verifiedAttackActionMsgs || []}
              setAction={setVerifiedAction}
              setMessages={setVerifiedMessages}
              value="verified"
              languages={data.languages || []}
              editorDisabled={planIsStarter || isStaticSalesDemo}
              integrations={integrations}
            />
            <div className="action-text">
              <span>
                When an incident is marked as
                <span className="sb"> Spam</span>
              </span>
            </div>
            <Select
              value={data.spamAction}
              onSelect={(obj) => updateSettingsField('spamAction', obj)}
              className="db"
              options={markAsSpamActionOptions}
              id="spamAction"
              optionFilterProp="label"
            />
          </div>
        </div>

        {atLeastStarterPlan(licenses.plan) && (!ff.ENABLE_AUTO_THEMIS || isMasterUser) && (
          <div className="incident-section" id="themis-threshold-section">
            <br />
            <div className={cls({ 'disabled-section': planIsStarter })}>
              <span className="label-name mb">Auto classification</span>
              <span className="email-text mb">
                Themis can automatically classify your incidents - saving you time, and improving
                incident response time.
                <br />
                Please select at what percentage you feel comfortable for Themis to automatically
                classify incidents.
              </span>
              <br />
              <Checkbox
                labelText={
                  <span>
                    Auto-classify if Themis is confident that the incident is a
                    <strong> phishing attack </strong>
                    at and above:
                  </span>
                }
                checked={!planIsStarter && data.autoClassificationPhishingEnabled}
                onChange={() =>
                  updateSettingsField(
                    'autoClassificationPhishingEnabled',
                    !data.autoClassificationPhishingEnabled
                  )
                }
                className="mb"
                id="Auto_Classify_Phishing"
              />
              <ControllerRange
                value={planIsStarter ? 80 : data.autoClassificationPhishingThreshold}
                enabled={data.autoClassificationPhishingEnabled && !planIsStarter}
                update={setPhishingThreshold}
                recommended={80}
                min={51}
                max={90}
                id="autoClassificationPhishing"
              />
              <br />
              <br />
              <Checkbox
                labelText={
                  <span>
                    Auto-classify if Themis is confident that the incident is
                    <strong> spam </strong>
                    at and above:
                  </span>
                }
                checked={!planIsStarter && data.autoClassificationSpamEnabled}
                onChange={() =>
                  updateSettingsField(
                    'autoClassificationSpamEnabled',
                    !data.autoClassificationSpamEnabled
                  )
                }
                className="mb"
                id="Auto_Classify_Spam"
              />
              <ControllerRange
                value={planIsStarter ? 70 : data.autoClassificationSpamThreshold}
                enabled={data.autoClassificationSpamEnabled && !planIsStarter}
                update={setSpamThreshold}
                recommended={70}
                min={51}
                max={90}
                id="autoClassificationSpam"
              />
              {!ff.ALWAYS_CLASSIFY_VIP_IMPERSONATION && (
                <>
                  <br />
                  <br />
                  <Checkbox
                    labelText={
                      <span>
                        Auto-classify incidents <b>suspected of VIP impersonation</b> if Themis is
                        confident that the incident is a phishing attack at and above:
                      </span>
                    }
                    checked={!planIsStarter && data.autoClassificationVipPhishingEnabled}
                    onChange={(event) => {
                      switchVipPhishingEnabled()
                      if (event.target.checked) {
                        setVipPhishingThreshold(80)
                      }
                    }}
                    className="mb"
                    id="Auto_Classify_Vip_Phishing"
                  />
                  <ControllerRange
                    key={data.autoClassificationVipPhishingEnabled}
                    value={planIsStarter ? 80 : data.autoClassificationVipPhishingThreshold}
                    enabled={data.autoClassificationVipPhishingEnabled && !planIsStarter}
                    update={setVipPhishingThreshold}
                    recommended={80}
                    min={50}
                    max={90}
                    id="autoClassificationVipPhishingThreshold"
                  />
                </>
              )}
              <div className="themis-info">
                <img src="/static/webapp/images/info-circle.svg" />
                <span className="dib-vac">
                  Themis’ algorithm may change as it is continuously learning and adapting.
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="incident-section">
          <span className="label-name mb">Email Snapshot</span>
          <div>
            <Checkbox
              id="Settings_Incident_Response_Email_Snapshot_Checkbox"
              className="mb mt"
              checked={data.emailBodyPreviewEnabled}
              onChange={() => {
                updateSettingsField('emailBodyPreviewEnabled', !data.emailBodyPreviewEnabled)
              }}
              labelText={
                <>
                  <b>Allow email snapshot</b> - allow users to see the incident's escalated email
                  snapshot
                </>
              }
            />
          </div>
        </div>
        <div className="incident-section">
          <span className="label-name mb">Unclassified Incidents</span>
          <span className="email-text mb">
            Select the default time frame that best fits your organization for displaying
            Unclassified Incidents.
          </span>
          <div className="select mb">
            <Select
              labelText="Default timeframe"
              value={
                data.defaultIncidentDateFilter ||
                INCIDENT_DATE_FILTERS_OPTIONS.find((option) => option.value === LAST_24_HOURS)
              }
              className="date-filter dib-vac"
              onChange={setDefaultIncidentDateFilter}
              options={INCIDENT_DATE_FILTERS_OPTIONS.filter((option) => option.value !== ALL_TIMES)}
              clearable={false}
              optionFilterProp="label"
              id="settings-threatProtection-timeFilter"
            />
          </div>
        </div>
        <div className="incident-section">
          <span className="label-name mb">Challenged Incident</span>
          <div>
            <Checkbox
              id="Settings_Incident_Response_Challenged_Incident_Checkbox"
              className="mb mt"
              checked={data.createChallengedIfSafeReported}
              onChange={() => {
                updateSettingsField(
                  'createChallengedIfSafeReported',
                  !data.createChallengedIfSafeReported
                )
              }}
              labelText="Create challenged incident when reporting an email classified as Safe"
            />
          </div>
        </div>

        <div
          className={cls('incident-section bni', {
            'disabled-section': planIsStarter,
          })}
        >
          <Checkbox
            checked={!planIsStarter && data.enablePhishingTags}
            onChange={() => updateSettingsField('enablePhishingTags', !data.enablePhishingTags)}
            labelText="Enable resolution tag classification for incidents marked as Phishing"
            className="mb mt"
            id="Enable_Resolution_Tag_Phishing"
          />
          <Checkbox
            checked={!planIsStarter && data.enableSafeTags}
            onChange={() => updateSettingsField('enableSafeTags', !data.enableSafeTags)}
            labelText="Enable resolution tag classification for incidents marked as Safe"
            className="mb"
            id="Enable_Resolution_Tag_Safe"
          />
          <Checkbox
            checked={!planIsStarter && data.enableSpamTags}
            onChange={() => updateSettingsField('enableSpamTags', !data.enableSpamTags)}
            labelText="Enable resolution tag classification for incidents marked as Spam"
            className="mb"
            id="Enable_Resolution_Tag_Spam"
          />
          <Checkbox
            checked={!planIsStarter && data.notifyReporter}
            onChange={() => updateSettingsField('notifyReporter', !data.notifyReporter)}
            labelText="Notify reporter of their status changes"
            className="mb"
            id="Notify_Reporter"
          />
          <Checkbox
            checked={!planIsStarter && data.deleteCalendarEvents}
            onChange={() => updateSettingsField('deleteCalendarEvents', !data.deleteCalendarEvents)}
            labelText="Delete meetings from calendars when meetings invites are classified as malicious"
            className="mb"
            id="Delete_Calendar_Events"
          />

          {!isGroupSettings && !groupSettingsNameByPartner && (
            <Button
              text="Email Notifications"
              onClick={() => history.push(URL_CONST.settings.endUserAlerts)}
              styleType="tertiary"
              RightIconDS={ArrowNext}
              dsIconStyles={{ fill: '#0152CC', height: '18px', width: '18px' }}
              id="Settings_Incident_Response_Email_Notifications_btn"
            />
          )}

          <div className="submit-container">
            <Button
              text="Save settings"
              onClick={permissions.admin && handleSubmit}
              disabled={!permissions.admin || groupSettingsNameByPartner || isStaticSalesDemo}
              id="save_settings"
            />
          </div>
          <div id="reporter-status-scroll" />
        </div>
      </div>
    </div>
  )
}

export default styled(IncidentResponseSettings)`
  padding: 20px 25px;
  height: calc(100% - 68px);
  min-width: 900px;
  .incident-section {
    padding: 40px 0;
    border-bottom: 1px solid #cacaca;
  }
  .label-name {
    color: ${(props) => props.theme.primaryColor};
    font-weight: bold;
    text-transform: capitalize;
    line-height: 3;
  }
  .email-text {
    display: block;
    font-size: 15px;
  }
  .threshold-field {
    width: 940px;
  }
  .response-field {
    width: 940px;
    .action-text {
      font-size: 13px;
      color: ${(props) => props.theme.ds_gray_3};
      margin-bottom: 5px;
    }
    .ant-select {
      max-width: 306px;
      width: 100%;
      margin-bottom: 21px;
    }
    .attack-container {
      margin-bottom: 20px;
      .disabled {
        pointer-events: none;
        opacity: 0.4;
      }
      .cke_bottom {
        display: none;
      }
      .cke_contents {
        height: 170px !important;
      }
      .cke_editable {
        line-height: 1.3 !important;
      }
    }
  }
  .select {
    margin-top: 15px;
    width: 200px;
  }
  .submit-container {
    position: fixed;
    bottom: 40px;
    right: 60px;
  }
  .themis-info {
    margin-top: 30px;
    img {
      width: 20px;
      height: 20px;
      margin-right: 10px;
    }
  }
`
